/**
 * usePriorityZIndex Hook
 * Manages z-index priority for canvas elements based on object type priority
 *
 * This hook:
 * - Creates a priority map from object types
 * - Updates element z-index only for changed priorities
 * - Uses typeId → element index for O(k) updates instead of O(n)
 */

import { useEffect, useMemo, useRef } from 'react'
import { dia } from '@joint/core'
import { ObjectType } from '../../../map-editor-shared/store/objectTypeStore'

interface LayerMapping {
  entityHandle: string
  typeId: string
}

interface UsePriorityZIndexProps {
  graph: dia.Graph | null
  types: ObjectType[]
  mappings: LayerMapping[] | null
  onUpdate?: () => void
}

export const usePriorityZIndex = ({ graph, types, mappings, onUpdate }: UsePriorityZIndexProps) => {
  // Create a priority map to track priority changes
  const priorityMap = useMemo(() => {
    const map = new Map<string, number>()
    types.forEach(type => {
      if (type.priority !== undefined) {
        map.set(type.id, type.priority)
      }
    })
    return map
  }, [types])

  // Track previous priorities to detect changes
  const prevPriorityRef = useRef<Map<string, number>>(new Map())

  // Build typeId → element IDs index
  const typeElementIndex = useMemo(() => {
    if (!graph || !mappings) return new Map<string, Set<string>>()

    const index = new Map<string, Set<string>>()
    const entityToTypeMap = new Map<string, string>()
    mappings.forEach(m => entityToTypeMap.set(m.entityHandle, m.typeId))

    const cells = graph.getCells()
    cells.forEach(cell => {
      if (!cell.isElement()) return
      const objectTypeId = cell.prop('objectTypeId')
      const entityHandle = cell.prop('data/entityHandle')

      let typeId: string | undefined
      if (objectTypeId) {
        typeId = objectTypeId
      } else if (entityHandle) {
        typeId = entityToTypeMap.get(entityHandle)
      }

      if (typeId) {
        if (!index.has(typeId)) index.set(typeId, new Set())
        index.get(typeId)!.add(cell.id as string)
      }
    })
    return index
  }, [graph, mappings])

  // Update element z-index only for types whose priority actually changed
  useEffect(() => {
    if (!graph) return

    // Find which typeIds had priority changes
    const changedTypeIds: string[] = []
    for (const [typeId, priority] of priorityMap.entries()) {
      if (prevPriorityRef.current.get(typeId) !== priority) {
        changedTypeIds.push(typeId)
      }
    }

    if (changedTypeIds.length === 0) {
      prevPriorityRef.current = new Map(priorityMap)
      return
    }

    let updateCount = 0

    for (const typeId of changedTypeIds) {
      const targetPriority = priorityMap.get(typeId)
      if (targetPriority === undefined) continue

      const elementIds = typeElementIndex.get(typeId)
      if (!elementIds) continue

      for (const elementId of elementIds) {
        const cell = graph.getCell(elementId)
        if (!cell || !cell.isElement()) continue
        const element = cell as dia.Element
        if (element.get('z') !== targetPriority) {
          element.set('z', targetPriority)
          updateCount++
        }
      }
    }

    prevPriorityRef.current = new Map(priorityMap)

    if (updateCount > 0 && onUpdate) {
      onUpdate()
    }
  }, [graph, priorityMap, typeElementIndex, onUpdate])

  return { priorityMap }
}
